import { Check, Copy, Loader2, RefreshCw, X } from "lucide-react";
import qrcode from "qrcode-generator";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import "./../style/TeamShareModal.scss";
import { generateInviteQR } from "../funtion";

interface TeamShareModalProps {
  open: boolean;
  onClose: () => void;
  teamName: string;
  team_id: string;
  joinCode: string;
  showSuccess?: boolean;
}

function buildQrDataUrl(value: string): string {
  const qr = qrcode(0, "M");
  qr.addData(value);
  qr.make();
  return qr.createDataURL(5, 2);
}

const TeamShareModal: React.FC<TeamShareModalProps> = ({
  open,
  onClose,
  teamName,
  team_id,
  showSuccess = false,
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  // Only the signed link from the API can actually add someone to the team. The old fallback
  // (`/join/<mock code>`) pointed at a route that does not exist, so we show loading / retry instead.
  const [invite, setInvite] = useState<{ status: "loading" | "ready" | "error"; url: string }>({ status: "loading", url: "" });
  const [attempt, setAttempt] = useState(0);

  const qrDataUrl = useMemo(() => (invite.url ? buildQrDataUrl(invite.url) : ""), [invite.url]);

  useEffect(() => {
    if (!open || !team_id || team_id === "0") return;
    let cancelled = false;
    setInvite({ status: "loading", url: "" });
    generateInviteQR(Number(team_id))
      .then((data) => {
        if (cancelled) return;
        setInvite(data?.qr_invite_url ? { status: "ready", url: data.qr_invite_url } : { status: "error", url: "" });
      })
      .catch(() => {
        if (!cancelled) setInvite({ status: "error", url: "" });
      });
    return () => {
      cancelled = true;
    };
  }, [team_id, open, attempt]);

  if (!open) return null;

  const handleCopy = async () => {
    if (!invite.url) return;
    try {
      await navigator.clipboard.writeText(invite.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("FailedToCopy"));
    }
  };



  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9000] p-4">
      <div className="team-share-modal rounded-xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
        <div className="team-share-modal__header shrink-0 flex justify-between items-center px-6 py-4 border-b">
          <h3 className="text-lg font-semibold m-0">
            {showSuccess ? t("TeamCreatedSuccess") : t("ShareTeam")}
          </h3>
          <button
            className="team-share-modal__close transition"
            onClick={onClose}
            aria-label={t("Close")}
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-2 flex flex-col items-center overflow-y-auto">
          <p className="w-full text-base font-semibold m-0 mb-1 text-left">{teamName}</p>
          <p className="team-share-modal__desc w-full text-sm text-left m-0 mb-4">
            {showSuccess ? t("TeamCreatedShareDesc") : t("ShareTeamDesc")}
          </p>

          <div className="team-share-modal__qr-box rounded-xl p-4 flex flex-col items-center mb-4">
            {invite.status === "ready" ? (
              <img src={qrDataUrl} alt={t("ScanToJoin")} className="w-32 h-32" />
            ) : invite.status === "loading" ? (
              <div className="w-32 h-32 flex items-center justify-center">
                <Loader2 size={28} className="animate-spin opacity-60" />
              </div>
            ) : (
              <div className="w-32 h-32 flex flex-col items-center justify-center gap-2 text-center">
                <span className="text-xs text-red-600">{t("InviteLinkFailed")}</span>
                <button
                  type="button"
                  onClick={() => setAttempt((a) => a + 1)}
                  className="flex items-center gap-1 text-xs font-medium text-[#e9b460] hover:underline"
                >
                  <RefreshCw size={12} /> {t("TryAgain")}
                </button>
              </div>
            )}
            <span className="team-share-modal__qr-label text-xs uppercase tracking-wider mt-2">
              {t("ScanToJoin")}
            </span>
          </div>

          <div className="w-full text-left">
            <label className="block text-sm font-medium mb-1">{t("ShareLink")}</label>
            <input
              readOnly
              value={invite.url}
              placeholder={invite.status === "loading" ? "…" : ""}
              onFocus={(e) => e.currentTarget.select()}
              className="team-share-modal__link-input w-full px-3 py-2.5 rounded-md text-sm mb-2"
            />
            <button
              onClick={handleCopy}
              disabled={invite.status !== "ready"}
              className={`team-share-modal__copy-btn w-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md text-sm font-medium transition ${copied
                  ? "team-share-modal__copy-btn--copied bg-green-100 text-green-700"
                  : "bg-[#e9b460] text-white hover:opacity-90"
                }`}
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? t("Copied") : t("Copy")}
            </button>
          </div>
        </div>

        <div className="team-share-modal__footer shrink-0 px-6 py-4 border-t">
          <button
            onClick={onClose}
            className="team-share-modal__done-btn w-full py-2.5 rounded-md text-sm font-medium transition"
          >
            {t("Done")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TeamShareModal;
